import moment from 'moment';
import { pickAmazonPrice } from './amazon-price';
import { calcFbaProfit } from './fba-profit';

/**
 * みんなの報告 1 件（LINE の仕入れ報告など、メンバーが貼った情報。らくじゃんの memberReports）。
 * ヤマダ・エディオンの在庫一覧に「LINE 誰が いつ 仕入値 店「コメント」」として出す（2026-10-14 ユーザー指定）
 */
export type MemberReport = {
  id: number;
  memberName: string;
  /** 報告日（M/D） */
  reportedAt: string;
  /** 仕入値（写真から読めなかった時は null） */
  price: number | null;
  storeName: string;
  comment: string;
  jan: string;
  /** 画面に出す 1 行。例: 「LINE shun 10/13 仕入 4,000 ヤマダ？「一旦下がると思いますが」」 */
  line: string;
  /** ここから下は、画面の下の一覧を在庫一覧と同じ形で出すための項目。らくじゃんに無い JAN なら asin・imageUrl・productName は空 */
  asin: string;
  imageUrl: string;
  productName: string;
  /** 在庫一覧と同じ決め方の Amazon 価格（無い時は -1） */
  amazonPrice: number;
  amazonPriceSource: 'current' | 'last' | 'none' | 'stale';
  lastKnownPriceAt?: string;
  boughtPastMonth?: number | null;
  /** Amazon 価格で FBA で売った場合の利益額・利益率（在庫一覧と同じ式。仕入値はこの報告の仕入値）。出せない時は null */
  fbaProfitPrice: number | null;
  fbaProfitRate: number | null;
};

/** 1 商品に出す報告の上限 */
export const MEMBER_REPORTS_PER_PRODUCT = 3;

/** memberReports API の 1 行のうち、使う項目 */
type LastKnownPrice = { price: number; at: string };

/** memberReports API の product.amazonProducts の 1 件のうち、使う項目 */
export type ReportAmazonProduct = {
  asin: string;
  isMain?: boolean | null;
  name?: string | null;
  imageUrl?: string | null;
  salesCommission?: number | null;
  categoryFee?: number | null;
  fbaShippingFee?: number | null;
  latestPrice?: number | null;
  lastKnownPrice?: LastKnownPrice | null;
  keepaProduct?: {
    boughtPastMonth?: number | null;
    latestPrice?: number | null;
    lastKnownPrice?: LastKnownPrice | null;
    keepaUpdatedAt?: string | null;
  } | null;
};

export type MemberReportRow = {
  id: number;
  jan: string;
  price?: number | null;
  storeName?: string | null;
  memberName: string;
  comment?: string | null;
  reportedAt: string;
  /** JAN が一致するらくじゃんの商品。無ければ null */
  product?: { jan?: string | null; amazonProducts?: readonly ReportAmazonProduct[] | null } | null;
};

/** 在庫一覧と同じく、メインの Amazon 商品（無ければ最初の 1 件） */
const pickAmazonProduct = (list: readonly ReportAmazonProduct[] | null | undefined): ReportAmazonProduct | null =>
  list?.find((ap) => ap.isMain) ?? list?.[0] ?? null;

/**
 * API の行から、店名に chainKeyword（「ヤマダ」「エディオン」）を含むものだけを報告にする。新しい順。
 * 店名が空の報告は、どの店か分からないのでどちらの画面にも出さない
 */
export function toMemberReports(rows: readonly MemberReportRow[], chainKeyword: string): MemberReport[] {
  return rows
    .filter((r) => (r.storeName ?? '').includes(chainKeyword))
    .map((r) => {
      const at = moment(new Date(r.reportedAt));
      const reportedAt = at.isValid() ? at.format('M/D') : '';
      const price = r.price ?? null;
      const storeName = r.storeName ?? '';
      const comment = r.comment ?? '';
      const line =
        ['LINE', r.memberName, reportedAt, price == null ? '' : `仕入 ${price.toLocaleString('ja-JP')}`, storeName]
          .filter((x) => x)
          .join(' ') + (comment ? `「${comment}」` : '');
      // 画面の下の一覧用: JAN が一致する商品から、在庫一覧と同じ決め方の Amazon 価格と FBA の利益（仕入値はこの報告の仕入値）
      const ap = pickAmazonProduct(r.product?.amazonProducts);
      const amazon = pickAmazonPrice(ap);
      const fba =
        price == null
          ? { profitPrice: null, profitRate: null }
          : calcFbaProfit({
              amazonPrice: amazon.price,
              cost: price,
              salesCommission: ap?.salesCommission,
              categoryFee: ap?.categoryFee,
              fbaShippingFee: ap?.fbaShippingFee,
            });
      return {
        report: {
          id: r.id,
          memberName: r.memberName,
          reportedAt,
          price,
          storeName,
          comment,
          jan: r.jan,
          line,
          asin: ap?.asin ?? '',
          imageUrl: ap?.imageUrl ?? '',
          productName: ap?.name ?? '',
          amazonPrice: amazon.price,
          amazonPriceSource: amazon.source,
          lastKnownPriceAt: amazon.at,
          boughtPastMonth: ap?.keepaProduct?.boughtPastMonth,
          fbaProfitPrice: fba.profitPrice,
          fbaProfitRate: fba.profitRate,
        },
        sortKey: at.isValid() ? at.valueOf() : 0,
      };
    })
    .sort((a, b) => b.sortKey - a.sortKey)
    .map((x) => x.report);
}

/**
 * 在庫一覧の各商品に、同じ JAN の報告を付ける（1 商品あたり MEMBER_REPORTS_PER_PRODUCT 件まで）。
 * 戻り値は、どの商品にも付かなかった報告（改廃リストに無い商品。画面の下の一覧に出す）
 */
export function attachMemberReports<T extends { jan: string; memberReports?: MemberReport[] }>(
  products: T[],
  reports: MemberReport[],
): MemberReport[] {
  const byJan = new Map<string, MemberReport[]>();
  for (const m of reports) {
    if (m.jan) byJan.set(m.jan, [...(byJan.get(m.jan) ?? []), m]);
  }
  const used = new Set<number>();
  for (const p of products) {
    const list = (p.jan && byJan.get(p.jan)) || [];
    p.memberReports = list.slice(0, MEMBER_REPORTS_PER_PRODUCT);
    for (const m of list) used.add(m.id);
  }
  return reports.filter((m) => !used.has(m.id));
}

/**
 * 在庫一覧の商品の行に付ける LINE 報告の 1 行。「LINE 誰が いつ」だけにする（2026-10-18 ユーザー指定）。
 * Amazon 価格・利益・店名はその行の列と同じなので出さない。報告の仕入値は、商品の仕入値（POS）と違う時だけ「仕入 N」を添える。
 * ひとことコメントがあれば「…」で続ける
 */
export function lineForProductRow(report: MemberReport, product: { cost: number }): MemberReport {
  const parts = ['LINE', report.memberName, report.reportedAt];
  if (report.price != null && report.price !== product.cost) {
    parts.push(`仕入 ${report.price.toLocaleString('ja-JP')}`);
  }
  const line = parts.filter((x) => x).join(' ') + (report.comment ? `「${report.comment}」` : '');
  return { ...report, line };
}
